import TagColumnBase from "./TagColumnBase";
import { ColumnStore } from "@bryntum/schedulerpro";
import { TAGS_KEYS } from "../../../other/constants/TagsKeys.jsx";
import { WIDGET_KEYS } from "../../../other/constants/WidgetKeys.jsx";
import { getFontAwesomeClassName } from "../../../other/utils/utils.jsx";
import {
  EDIT_MESSAGES,
  ICON_COL_DEF_WIDTH,
  NO_VALUE,
  RESOURCE_FONT_SIZE,
  VIEW_TYPES
} from "../../../other/constants/OtherKeys.jsx";
import { SWIVU_STANDARD_ICONS } from "../../../other/constants/IconKeys.jsx";
import { GRANTS } from "../../../other/constants/PrivilegeTypes.jsx";
import { STORE_KEYS } from "../../../other/constants/StoreKeys.jsx";
import { userPreferences } from "../../../react/context/bryntumContext.jsx";
import { PERSISTENCE_STATE_KEYS } from "../../../other/constants/PersistenceStateKeys.jsx";
import SwivuSortColumn from "../SwivuSortColumn.jsx";

const storeKey = "resourceTagValues";

class ResourceTagsColumn extends TagColumnBase {
  static get $name() {
    return "ResourceTagsColumn";
  }

  static get type() {
    return WIDGET_KEYS.columnResourceTag;
  }

  static get defaults() {
    return this.defaultFilterableOptions;
  }

  get tagRecordKey() {
    return "_tags";
  }

  get storeKey() {
    return storeKey;
  }

  get tagKey() {
    return TAGS_KEYS.tag;
  }


  get swivuListTitle() {
    return "Resource Tags";
  }

  get canMultiSelectSwivuList() {
    return true;
  }
}

class ResourceStatusColumn extends TagColumnBase {
  static get $name() {
    return "ResourceStatusColumn";
  }

  static get type() {
    return WIDGET_KEYS.columnResourceStatus;
  }

  static get defaults() {
    return this.defaultFilterableOptions;
  }

  get tagRecordKey() {
    return "_status";
  }

  get storeKey() {
    return storeKey;
  }

  get tagKey() {
    return TAGS_KEYS.status;
  }



  get swivuListTitle() {
    return "Resource Status";
  }
}

class ResourceCategoryColumn extends TagColumnBase {
  static get $name() {
    return "ResourceCategoryColumn";
  }

  static get type() {
    return WIDGET_KEYS.columnResourceCategory;
  }

  static get defaults() {
    return this.defaultFilterableOptions;
  }

  get tagRecordKey() {
    return "_category";
  }

  get storeKey() {
    return storeKey;
  }

  get tagKey() {
    return TAGS_KEYS.category;
  }


  get swivuListTitle() {
    return "Resource Category";
  }
}

class ResourceTagIconColumn extends TagColumnBase {
  static get $name() {
    return "IconColumn";
  }

  static get type() {
    return WIDGET_KEYS.columnResourceTagIcon;
  }

  static get defaults() {
    return {
      text: "Icon",
      htmlEncode: false,
      maxWidth: "9em",
      minWidth: ICON_COL_DEF_WIDTH,
      align: "center",
      cls: "swivu-resource-icon-column-header swivu-icon-column",
      renderer({ record }) {
        if (userPreferences) {
          const iconSizeValue = userPreferences.getSetting(PERSISTENCE_STATE_KEYS.resourceIconSize);
          const iconSize = RESOURCE_FONT_SIZE[iconSizeValue];
          const displayIconClass = !record[this.field] ? SWIVU_STANDARD_ICONS[VIEW_TYPES.resources] : `fa ${getFontAwesomeClassName(record[this.field])}`;
          return {
            children: [
              {
                tag: "i",
                class: `${displayIconClass} ${iconSize}`,
                style: `color: ${record.teamColor}`
              }
            ]
          };
        }
      },
      groupRenderer({ groupRowFor }) {
        if (userPreferences) {
          const iconSizeValue = userPreferences.getSetting(PERSISTENCE_STATE_KEYS.resourceIconSize);
          const iconSize = RESOURCE_FONT_SIZE[iconSizeValue];
          if (groupRowFor === NO_VALUE) {
            return {
              tag: "i",
              class: `${SWIVU_STANDARD_ICONS[VIEW_TYPES.resources]} ${iconSize || ""}`,
            }
          } else {
            return {
              tag: "i",
              class: `fa ${getFontAwesomeClassName(groupRowFor)} ${iconSize || ""}`,
            }
          }
        }
      },
      headerRenderer() {
        const iconSizeValue = userPreferences?.getSetting(PERSISTENCE_STATE_KEYS.resourceIconSize);
        const iconSize = RESOURCE_FONT_SIZE[iconSizeValue];
        return `<i class="${SWIVU_STANDARD_ICONS[VIEW_TYPES.resources]} ${iconSize || ""} flex justify-center items-center !me-0 w-full h-full" />`;
      },
      filterable: false,
      editor: {
        ...this.defaultFilterableOptions,
        name: "_icon",
        type: WIDGET_KEYS.fieldIcon,
        editMessage: EDIT_MESSAGES.icon,
      }
    };
  }

  getFilterStore(project) {
    return this.getTagStore(project, storeKey, TAGS_KEYS.icon);
  }

  get swivuListTitle() {
    return "Resource Icon"
  }

  get swivuListItems() {
    return this.project.getCrudStore(STORE_KEYS.resourceTagValuesStore).query((record) => record.tagSet === TAGS_KEYS.icon, true)
  }

  getSwivuListItemDisplayValue(record) {
    return `<i class="fa ${getFontAwesomeClassName(record.tagValue)}"></i>`;
  }

  getSelectedSwivuListItem(record) {
    return record._icon;
  }

  onSwivuListSelectionChange(record, [icon]) {
    record._icon = icon;
  }

  onSwivuManageList() {
    this.viewManager.openAnonymousViewByType({ viewType: VIEW_TYPES.settings, viewName: 'Settings' });
  }

  onLoad(project, otherParams) {
    super.onLoad(project, otherParams);
    const { editorHandler, viewManager } = otherParams;
    this.project = project;
    this.editorHandler = editorHandler;
    this.viewManager = viewManager;
    if (this.userPreferencesStoreListener) {
      this.userPreferencesStoreListener();
      this.userPreferencesStoreListener = null;
    }

    this.userPreferencesStoreListener = this.project.userPreferencesStore.on('update', (e) => {
      if (e.record.key === PERSISTENCE_STATE_KEYS.resourceIconSize) {
        this.grid?.refreshHeaders()
      }
    });
  }
}

class ResourceBlackoutColumn extends SwivuSortColumn {
  static get $name() {
    return "ResourceBlackoutColumn";
  }

  static get type() {
    return WIDGET_KEYS.columnResourceBlackout;
  }

  static get defaults() {
    return {
      htmlEncode: false,
      maxWidth: "9em",
      minWidth: ICON_COL_DEF_WIDTH,
      align: "center",
      cellCls: "p-0 cursor-pointer",
      cls: "swivu-icon-column",
      field: "hideAgenda",
      flex: 1,
      text: "Blackout",
      disabled: true,
      groupRenderer({ groupRowFor }) {
        const colorClass = groupRowFor ? "text-[#ff4646]": "text-primary";
        return {
          class: "flex gap-1",
          children: [
            { tag: "i",  class: `fa-regular fa-lightbulb-slash ${colorClass}` },
            { text: `Blackout ${groupRowFor && groupRowFor !== NO_VALUE ? "On - Hidden from Workforce": "Off - Visible to Workforce"}` }
          ]
        };
      },
      renderer({ record }) {
        const colorClass = record.hideAgenda ? "text-[#ff4646]": "text-primary";
        const cls = record.hideAgenda ? "fa-regular fa-lightbulb-slash": "fa-light fa-lightbulb-on";
        return {
          children: [{ tag: "i",  class: `${cls} ${colorClass}` }]
        };
      },
      filterable: false,
      editor: false,
    };
  }

  // this is called from grid CellTooltip feature to retrieve text to display on tooltip
  swivuTooltip({ record }) {
    return record.hideAgenda ? "Excluded from external schedules.": "Included within external schedules."
  }

  onCellClick({ record }) {
    if (record.isGroupHeader || !this.swivuCanToggleButton) return;
    record.hideAgenda = !record.hideAgenda
  }

  onLoad(project, otherParams) {
    super.onLoad(project, otherParams);
    this.swivuCanToggleButton = otherParams?.user && otherParams?.user.can(GRANTS.grant_four);
  }
}

ColumnStore.registerColumnType(ResourceCategoryColumn);
ColumnStore.registerColumnType(ResourceStatusColumn);
ColumnStore.registerColumnType(ResourceTagsColumn);
ColumnStore.registerColumnType(ResourceTagIconColumn);
ColumnStore.registerColumnType(ResourceBlackoutColumn);
